import { Action } from '../actions/favorites';
import getFavoriteListDisplayName from '../../lib/utils/getFavoriteListDisplayName';

const defaultState = {
  favoriteLists: undefined,
  isSaving: false,
  isFetching: false,
  isDeleting: false,
  error: undefined,
  favoriteStationsByListId: {},
  isFetchingStations: false,
};

export default function favoritesReducer(state = defaultState, action) {
  const { favoriteLists, favoriteStationsByListId } = state;

  switch (action?.type) {
    case Action.Favorites.FetchFavoriteListsPending:
      return {
        ...state,
        isFetching: true,
        error: undefined,
      };

    case Action.Favorites.FetchFavoriteListsCompleted:
      return {
        ...state,
        isFetching: false,
        favoriteLists: action.payload.favoriteLists,
      };

    case Action.Favorites.FetchFavoriteListsFailed:
      return {
        ...state,
        isFetching: false,
        error: 'There was an error fetching your lists. Please try again later.',
      };

    case Action.Favorites.CreateFavoriteListPending:
    case Action.Favorites.UpdateFavoriteListPending:
    case Action.Favorites.AddStationToFavoritesPending:
      return {
        ...state,
        isSaving: true,
        error: undefined,
      };

    case Action.Favorites.CreateFavoriteListCompleted:
      return {
        ...state,
        isSaving: false,
        favoriteLists: [].concat(
          // set existing lists as non-default if new list is marked as default
          favoriteLists.map(list => ({
            ...list,
            ...!!action.payload.list.is_default && {
              is_default: false,
              display_name: list.name,
            },
          })),
          { ...action.payload.list, display_name: getFavoriteListDisplayName(action.payload.list) },
        ),
      };

    case Action.Favorites.CreateFavoriteListFailed:
      return {
        ...state,
        isSaving: false,
        error: 'There was an error creating your list.',
      };

    case Action.Favorites.UpdateFavoriteListCompleted:
      return {
        ...state,
        isSaving: false,
        favoriteLists: favoriteLists.map((list) => {
          // there can only be one default list
          if (list.id === action.meta.listId) {
            return action.payload.list;
          }

          // if user updates edited list to default make sure to mark others as non-default
          // Also, make sure to update list display names removing ` (Default list)` where necessary
          const updated = action.meta.isUpdatingDefaultList ? { ...list, is_default: false } : list;
          return {
            ...updated,
            display_name: getFavoriteListDisplayName(updated),
          };
        }),
      };

    case Action.Favorites.UpdateFavoriteListFailed:
      return {
        ...state,
        isSaving: false,
        error: 'There was an error updating your list.',
      };

    case Action.Favorites.DeleteFavoriteListPending:
    case Action.Favorites.DeleteStationFromFavoritesPending:
      return {
        ...state,
        isDeleting: true,
        error: undefined,
      };

    case Action.Favorites.DeleteFavoriteListCompleted:
      return {
        ...state,
        isDeleting: false,
        favoriteLists: favoriteLists.filter(list => list.id !== action.meta.listId),
      };

    case Action.Favorites.DeleteFavoriteListFailed:
      return {
        ...state,
        isDeleting: false,
        error: 'There was an error deleting your list',
      };

    case Action.Favorites.FetchFavoriteStationsPending:
      return {
        ...state,
        isFetchingStations: true,
      };

    case Action.Favorites.FetchFavoriteStationsCompleted:
      return {
        ...state,
        isFetchingStations: false,
        favoriteStationsByListId: {
          ...favoriteStationsByListId,
          [action.meta.listId]: (favoriteStationsByListId[action.meta.listId] || []).concat(
            state.favoriteLists.find(list => list.id === action.meta.listId).poi_ids.map(
              stationId => action.payload.stations.find(s => s.id === stationId),
            ).filter(s => !!s?.id),
          ),
        },
      };

    case Action.Favorites.FetchFavoriteStationsFailed:
      return {
        ...state,
        isFetchingStations: false,
        error: 'There was an error fetching your favorite stations in this list',
      };

    case Action.Favorites.AddStationToFavoritesCompleted:
      return {
        ...state,
        isSaving: false,
        favoriteLists: favoriteLists.map((list) => {
          if (list.id === action.meta.listId) {
            return {
              ...list,
              poi_ids: [
                ...list.poi_ids,
                action.meta.stationId,
              ],
            };
          }
          return list;
        }),
        favoriteStationsByListId: {
          ...favoriteStationsByListId,
          [action.meta.listId]: [
            ...favoriteStationsByListId[action.meta.listId] || [],
            ...action.payload.stations,
          ],
        },
      };

    case Action.Favorites.AddStationToFavoritesFailed:
      return {
        ...state,
        isSaving: false,
        error: 'There was an error adding station to your favorites.',
      };

    case Action.Favorites.DeleteStationFromFavoritesCompleted:
      return {
        ...state,
        isDeleting: false,
        favoriteLists: favoriteLists.map((list) => {
          if (list.id === action.meta.listId) {
            return {
              ...list,
              poi_ids: list.poi_ids.filter(poiId => poiId !== action.meta.stationId),
            };
          }
          return list;
        }),
        favoriteStationsByListId: {
          ...favoriteStationsByListId,
          [action.meta.listId]: favoriteStationsByListId[action.meta.listId]
            ?.filter(s => s && s.id !== action.meta.stationId),
        },
      };

    case Action.Favorites.DeleteStationFromFavoritesFailed:
      return {
        ...state,
        isDeleting: false,
        error: 'There was an error deleting station from your favorites.',
      };

    default:
      return state;
  }
}
